import type { Event, GuestListEntry, RecipientGroup, RSVP } from '@/types';
import { formatCurrency } from '@/lib/utils';
import {
  getRsvpExpectedAmount,
  isInviteeCoveredByInviter,
  shouldIncludeRsvpInUnpaidAnnouncementGroup,
} from '@/lib/event-utils';

export type AnnouncementRecipientSource = 'rsvp' | 'subscriber' | 'unit-member';

export interface AnnouncementRecipientRow {
  email: string;
  name: string;
  source: AnnouncementRecipientSource;
  /** Formatted ticket / expected amount for RSVP rows; omitted for other sources. */
  ticketPriceFormatted?: string | null;
  /** Human-readable payment state for RSVP rows; omitted for other sources. */
  paidStatusLabel?: string | null;
}

function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

function guestDisplayName(g: GuestListEntry): string {
  const fromParts = [g.firstName, g.lastName].filter(Boolean).join(' ').trim();
  if (fromParts) {
    return fromParts;
  }
  return g.name?.trim() || 'Guest';
}

function rsvpTicketPriceLabel(
  rsvp: RSVP,
  event: Event | null,
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>,
): string | null {
  if (!event) {
    return null;
  }
  if (rsvp.invoiceId) {
    const inv = invoiceById.get(rsvp.invoiceId);
    if (inv) {
      return formatCurrency(Math.max(0, inv.outstandingBalance ?? 0));
    }
  }
  return formatCurrency(getRsvpExpectedAmount(rsvp, event));
}

/** Payment label for organiser-facing recipient preview (aligned with invoices and expected amount). */
export function rsvpPaidStatusLabelForAnnouncement(
  rsvp: RSVP,
  event: Event | null,
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>
): string | null {
  if (!event) {
    return null;
  }
  if (rsvp.apologies === true) {
    return 'Apologies';
  }
  if (rsvp.status === 'declined') {
    return 'Declined';
  }
  if (isInviteeCoveredByInviter(rsvp)) {
    return 'Host pays';
  }
  const expected = getRsvpExpectedAmount(rsvp, event);
  if (expected === 0) {
    return 'No charge';
  }
  if (rsvp.invoiceId) {
    const inv = invoiceById.get(rsvp.invoiceId);
    if (inv?.status === 'pending_verification') {
      return 'Pending verification';
    }
    if (inv && (inv.outstandingBalance ?? 0) <= 0) {
      return 'Paid';
    }
    if (inv) {
      return 'Unpaid';
    }
  }
  return rsvp.paid ? 'Paid' : 'Unpaid';
}

/**
 * Builds the deduplicated recipient list for the current send-to selection, matching Cloud Function groups.
 * Unit member rows must be resolved separately (Firestore user profiles) and passed in.
 */
export function buildAnnouncementRecipientsList(
  sendTo: RecipientGroup[],
  rsvps: RSVP[],
  event: Event | null,
  invoiceById: Map<string, { status?: string; outstandingBalance?: number }>,
  guestList: GuestListEntry[],
  unitMemberRows: { email: string; name: string }[]
): AnnouncementRecipientRow[] {
  const map = new Map<string, AnnouncementRecipientRow>();

  const add = (email: string, name: string, source: AnnouncementRecipientSource, rsvp?: RSVP) => {
    const key = normaliseEmail(email);
    if (!key) {
      return;
    }
    if (!map.has(key)) {
      const row: AnnouncementRecipientRow = {
        email: email.trim(),
        name: (name || 'Guest').trim() || 'Guest',
        source,
      };
      if (source === 'rsvp' && rsvp) {
        row.ticketPriceFormatted = rsvpTicketPriceLabel(rsvp, event, invoiceById);
        row.paidStatusLabel = rsvpPaidStatusLabelForAnnouncement(rsvp, event, invoiceById);
      }
      map.set(key, row);
    }
  };

  if (sendTo.length === 0) {
    return [];
  }

  for (const group of sendTo) {
    if (group === 'all') {
      // Mirrors the backend's 'all' group exactly (sendAnnouncementEmail): every
      // RSVP except cancelled/declined, not literally every RSVP document.
      rsvps
        .filter((r) => r.status !== 'cancelled' && r.status !== 'declined')
        .forEach((r) => {
          if (r.email?.trim()) {
            add(r.email, r.name || 'Guest', 'rsvp', r);
          }
        });
    } else if (group === 'confirmed') {
      rsvps
        .filter((r) => r.status === 'confirmed')
        .forEach((r) => {
          if (r.email?.trim()) {
            add(r.email, r.name || 'Guest', 'rsvp', r);
          }
        });
    } else if (group === 'waitlist') {
      rsvps
        .filter((r) => r.status === 'waitlist')
        .forEach((r) => {
          if (r.email?.trim()) {
            add(r.email, r.name || 'Guest', 'rsvp', r);
          }
        });
    } else if (group === 'unpaid') {
      rsvps
        .filter((r) => shouldIncludeRsvpInUnpaidAnnouncementGroup(r, event, invoiceById))
        .forEach((r) => {
          if (r.email?.trim()) {
            add(r.email, r.name || 'Guest', 'rsvp', r);
          }
        });
    } else if (group === 'subscribers') {
      guestList.forEach((g) => {
        if (g.email?.trim()) {
          add(g.email, guestDisplayName(g), 'subscriber');
        }
      });
    } else if (group === 'unit-members') {
      unitMemberRows.forEach((row) => {
        if (row.email?.trim()) {
          add(row.email, row.name, 'unit-member');
        }
      });
    }
  }

  const list = Array.from(map.values());
  list.sort((a, b) => {
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    if (byName !== 0) {
      return byName;
    }
    return a.email.localeCompare(b.email, undefined, { sensitivity: 'base' });
  });
  return list;
}

export function recipientSourceLabel(source: AnnouncementRecipientSource): string {
  switch (source) {
    case 'rsvp':
      return 'Attendee';
    case 'subscriber':
      return 'Subscribed guest';
    case 'unit-member':
      return 'Unit member';
    default:
      return source;
  }
}
